import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { menuRoutes as navigationRoutes } from 'src/app/routes/routes';
import { DrawerService } from 'src/app/services/drawer.service';
import { ManagedLoginService } from 'src/app/services/managed-login.service';

/**
 * Root component for the Alimentos & Bebidas application, coordinating layout,
 * top navigation toolbar, collapsible side drawer, authentication state, and route changes.
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _unsubscribeOnDestroy$: Subject<boolean> =
    new Subject<boolean>();
  isExpanded = true;
  matchesTemp = true;

  menuRoutes = navigationRoutes;

  openDrawer = false;
  isTokenReady = false;

  /**
   * Initializes the AppComponent with change detection, layout drawer, authentication, and routing services.
   *
   * @param changeDetectorRef - Service triggering manual view check cycles.
   * @param drawerLayoutService - Service managing layout drawer state and visibility.
   * @param managedLoginService - Service handling authentication and token refresh operations.
   * @param router - Angular Router for observing route transitions and template active route checks.
   */
  /**
   * Initializes the AppComponent with change detection, layout drawer, authentication, and routing services.
   *
   * @param changeDetectorRef - Service triggering manual view check cycles.
   * @param drawerLayoutService - Service managing layout drawer state and visibility with localStorage persistence.
   * @param managedLoginService - Service handling authentication and token refresh operations.
   * @param router - Angular Router for observing route transitions and template active route checks.
   */
  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    public readonly drawerLayoutService: DrawerService,
    private readonly managedLoginService: ManagedLoginService,
    public readonly router: Router,
  ) {
    this.drawerLayoutService.drawerVisibility
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe((currentLayoutState) => {
        this.openDrawer = currentLayoutState;
        this.changeDetectorRef.markForCheck();
      });
  }

  /**
   * Synchronizes drawer opened/closed event from mat-drawer with DrawerService and localStorage.
   *
   * @param isOpened - Updated boolean state of the drawer.
   */
  public handleDrawerOpenedChange(isOpened: boolean): void {
    if (this.openDrawer !== isOpened) {
      this.drawerLayoutService.setDrawerVisibility(isOpened, true);
    }
  }

  /**
   * Toggles the side drawer and saves the preference in localStorage.
   */
  public handleToggleDrawer(): void {
    this.drawerLayoutService.toggleDrawer();
  }

  /**
   * Initializes the component on startup, verifying authentication tokens and scheduling token refreshes.
   *
   * @returns Promise<void>
   * @throws None
   */
  async ngOnInit(): Promise<void> {
    const idToken = window.localStorage.getItem('id_token');
    const refreshToken = window.localStorage.getItem('refresh_token');

    if (idToken && refreshToken) {
      if (this.managedLoginService.isTokenExpired()) {
        await this.managedLoginService.refreshToken();
      } else {
        this.managedLoginService.scheduleTokenRefresh();
      }
    }
    this.isTokenReady = true;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Cleans up active RxJS subscriptions before component destruction to prevent memory leaks.
   *
   * @returns void
   * @throws None
   */
  ngOnDestroy(): void {
    this._unsubscribeOnDestroy$.next(true);
    this._unsubscribeOnDestroy$.complete();
  }

  /**
   * Terminates the current user session by clearing tokens and redirecting to the login provider.
   *
   * @returns void
   * @throws None - Catches any internal logout errors and logs them to the console.
   */
  signOut(): void {
    try {
      this.managedLoginService.signOut();
    } catch (error) {
      console.error('Error during sign out process:', error);
    }
  }
}
